import { deviceType } from '@/core/environment/deviceType';
import { useTracker } from '@/core/tracking';
import { getPico } from '@/core/tracking/pico';
import UpdateCompletedModal from '@/features/settings/secretMenu/UpdateCompletedModal';
import { useSettingsService } from '@/layout/appWrapper/ServiceProvider';
import { Experiment } from '@/services/settingsService';
import { localeState } from '@/stores/localizationStore';
import { closeModalSelector, pushModalSelector } from '@/stores/modalStore';
import Spinner from '@/ui/content/Spinner';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';

interface Props {
  modalId: string;
  password: string;
}

export const DESEGMENTED_SEGMENT_INDEX = -1;

/* Reproduce the snake->camel key transformation applied by the HTTP layer. */
export const toCamelCase = (text: string): string => {
  const keyParts = text.split('_');
  return (
    keyParts[0] +
    keyParts
      .slice(1)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join('')
  );
};

/* Build the initial/current segmentation maps keyed by raw experiment names.
 * The identity keys are camel-cased by the HTTP layer and that transformation
 * is lossy, so raw names are camel-cased forward instead of reversing it. */
export const matchUserSegmentations = (
  experiments: Pick<Experiment, 'name'>[],
  segmentations: Record<string, { index: number; state?: string }>
): Record<string, number> => {
  const segmentationsByCamelName: Record<
    string,
    { index: number; state?: string }
  > = {};
  Object.entries(segmentations).forEach(([experimentName, segment]) => {
    const camelName = toCamelCase(experimentName);
    if (!(camelName in segmentationsByCamelName)) {
      segmentationsByCamelName[camelName] = segment;
    }
  });

  const userSegmentations: Record<string, number> = {};
  experiments.forEach(({ name: rawExperimentName }) => {
    const segment = segmentationsByCamelName[toCamelCase(rawExperimentName)];
    if (
      segment &&
      (segment.state === 'running' || segment.state === 'observing')
    ) {
      userSegmentations[rawExperimentName] = segment.index;
    }
  });
  return userSegmentations;
};

export default function ExperimentsHandler({ modalId, password }: Props) {
  const locale = useRecoilValue(localeState);
  const device = deviceType();
  const localStorageId = getPico().getLocalStorageId();

  const closeModal = useSetRecoilState(closeModalSelector);
  const pushModal = useSetRecoilState(pushModalSelector);
  const { retrieveExperiments, retrieveSettings, forceExperiments } =
    useSettingsService();

  const [initialExperiments, setInitialExperiments] = useState<
    Record<string, number>
  >({});
  const [currentExperiments, setCurrentExperiments] = useState<
    Record<string, number>
  >({});

  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [compatibleExperiments, setCompatibleExperiments] =
    useState<Experiment[]>();

  const { track } = useTracker();

  useEffect(() => {
    const fetchCompatibleExperiments = async () => {
      setIsFetching(true);

      /* The retrieve endpoint does not include the user's current segment, so
       * the identity response is fetched in parallel. Both settle before the
       * form becomes interactive, so pre-selection cannot overwrite user
       * choices, and an identity failure only degrades pre-selection. */
      const [experimentsResult, identityResult] = await Promise.allSettled([
        retrieveExperiments(password, localStorageId, device, locale),
        retrieveSettings(localStorageId, device, locale),
      ]);

      if (identityResult.status === 'fulfilled') {
        const userSegmentations = matchUserSegmentations(
          experimentsResult.status === 'fulfilled'
            ? experimentsResult.value
            : [],
          identityResult.value.segmentations
        );

        setInitialExperiments(userSegmentations);
        setCurrentExperiments(userSegmentations);
      } else {
        console.error(identityResult.reason);
      }

      if (experimentsResult.status === 'fulfilled') {
        setCompatibleExperiments(experimentsResult.value);
      } else {
        console.error(experimentsResult.reason);
      }

      setIsFetching(false);
    };

    fetchCompatibleExperiments().catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale, password, retrieveExperiments]);

  const selectSegment = useCallback((experimentName: string, index: number) => {
    setCurrentExperiments((prev) => ({ ...prev, [experimentName]: index }));
  }, []);

  const deselectExperiment = useCallback((experimentName: string) => {
    setCurrentExperiments((prev) => ({
      ...prev,
      [experimentName]: DESEGMENTED_SEGMENT_INDEX,
    }));
  }, []);

  /* Only the experiments whose selection differs from the initial one are
   * stored: sending everything would needlessly force segmentations the user
   * never touched. */
  const modifiedExperiments = useMemo(() => {
    const changes: Record<string, number> = {};
    Object.entries(currentExperiments).forEach(
      ([experimentName, segmentIndex]) => {
        if (initialExperiments[experimentName] !== segmentIndex) {
          changes[experimentName] = segmentIndex;
        }
      }
    );
    return changes;
  }, [currentExperiments, initialExperiments]);

  return isFetching ? (
    <div className="my-8 flex justify-center">
      <Spinner />
    </div>
  ) : compatibleExperiments ? (
    <>
      {compatibleExperiments.map(
        ({
          id: experimentId,
          state,
          description,
          name: experimentName,
          segments,
        }) => {
          const currentSegmentIndex = currentExperiments[experimentName];
          const initialSegmentIndex = initialExperiments[experimentName];
          return (
            <div key={`exp_${experimentId}`} className="w-full pb-8">
              <h3 className="SB16 text-gray-900">
                {experimentName} ({state})
              </h3>
              <p className="R12 text-gray-500 mb-4">{description}</p>
              <ul className="w-full bg-white rounded-lg border">
                <li
                  key={`${experimentId}_no_segmentation`}
                  className="w-full mx-2"
                >
                  <div className="flex items-center">
                    <input
                      id={`${experimentId}_no_segmentation`}
                      type="radio"
                      name={experimentId}
                      value="de-segmented"
                      checked={
                        currentSegmentIndex !== undefined &&
                        currentSegmentIndex === DESEGMENTED_SEGMENT_INDEX
                      }
                      onChange={() => deselectExperiment(experimentName)}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 focus:ring-2"
                    />
                    <label
                      htmlFor={`${experimentId}_no_segmentation`}
                      className="M14 py-3 ms-2 w-full text-gray-900"
                    >
                      de-segmented (removes the segmentation and excludes from
                      automatic segmentation)
                    </label>
                  </div>
                </li>
                {segments.map(
                  ({ id: segmentId, index, name: segmentName, weight }) => (
                    <li key={`seg_${segmentId}`} className="w-full mx-2">
                      <div className="flex items-center">
                        <input
                          id={segmentId}
                          type="radio"
                          name={experimentId}
                          value={segmentName}
                          checked={currentSegmentIndex === index}
                          onChange={() => selectSegment(experimentName, index)}
                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 focus:ring-2"
                        />
                        <label
                          htmlFor={segmentId}
                          className="M14 py-3 ms-2 w-full text-gray-900"
                        >
                          {segmentName} ({weight! * 100}%)
                          {initialSegmentIndex === index && (
                            <span className="text-gray-400"> · current</span>
                          )}
                        </label>
                      </div>
                    </li>
                  )
                )}
              </ul>
            </div>
          );
        }
      )}
      <div className="mt-5 flex justify-end items-center">
        <button
          className="btn btn--large btn--black"
          onClick={async () => {
            if (Object.keys(modifiedExperiments).length > 0) {
              await forceExperiments(
                password,
                modifiedExperiments,
                localStorageId,
                device,
                locale
              );
            }

            track('secret_menu', 'experiments_updated');
            closeModal(modalId);

            pushModal({
              id: 'experiments-update',
              Modal: UpdateCompletedModal,
            });
          }}
        >
          Store new selection
        </button>
      </div>
    </>
  ) : (
    <p className="py-2">No experiments are currently running</p>
  );
}
